import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { webColors, webBorderRadius, webShadows } from '../../constants/webStyles';

const WebCard = ({
  children,
  variant = 'default',
  onPress,
  style,
  header,
  footer,
  noPadding = false,
}) => {
  const cardStyles = [
    styles.card,
    variant === 'elevated' && styles.cardElevated,
    variant === 'outlined' && styles.cardOutlined,
    variant === 'flat' && styles.cardFlat,
    noPadding && styles.noPadding,
    style,
  ];

  const CardWrapper = onPress ? Pressable : View;
  const wrapperProps = onPress
    ? {
        onPress,
        style: ({ hovered }) => [
          ...cardStyles,
          hovered && styles.cardHover,
        ],
      }
    : { style: cardStyles };

  return (
    <CardWrapper {...wrapperProps}>
      {header && (
        <View style={styles.cardHeader}>
          {typeof header === 'string' ? (
            <Text style={styles.cardHeaderText}>{header}</Text>
          ) : (
            header
          )}
        </View>
      )}
      <View style={[styles.cardContent, noPadding && styles.noPadding]}>
        {children}
      </View>
      {footer && <View style={styles.cardFooter}>{footer}</View>}
    </CardWrapper>
  );
};

// Subcomponents for structured card content
WebCard.Title = ({ children, style }) => (
  <Text style={[styles.title, style]}>{children}</Text>
);

WebCard.Subtitle = ({ children, style }) => (
  <Text style={[styles.subtitle, style]}>{children}</Text>
);

WebCard.Body = ({ children, style }) => (
  <Text style={[styles.body, style]}>{children}</Text>
);

WebCard.Actions = ({ children, style }) => (
  <View style={[styles.actions, style]}>{children}</View>
);

const styles = StyleSheet.create({
  card: {
    backgroundColor: webColors.cardBackground,
    borderRadius: webBorderRadius.large,
    ...webShadows.card,
    overflow: 'hidden',
  },
  cardElevated: {
    ...webShadows.cardHover,
  },
  cardOutlined: {
    shadowColor: 'transparent',
    shadowOpacity: 0,
    elevation: 0,
    borderWidth: 1,
    borderColor: webColors.border,
  },
  cardFlat: {
    shadowColor: 'transparent',
    shadowOpacity: 0,
    elevation: 0,
    backgroundColor: webColors.background,
  },
  cardHover: {
    ...webShadows.cardHover,
    transform: [{ translateY: -2 }],
  },
  noPadding: {
    padding: 0,
  },
  cardHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },
  cardHeaderText: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  cardContent: {
    padding: 16,
  },
  cardFooter: {
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: webColors.border,
    backgroundColor: webColors.background,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: webColors.textSecondary,
    marginBottom: 8,
  },
  body: {
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
});

export default WebCard;
